import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, DatePicker, Select, Tag, message, Dropdown, Radio } from "antd";
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import useRequest from "../../hooks/useRequest";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/langHelper";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

const PERIOD_OPTIONS = [
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "28", label: "28 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "365 days" },
  { value: "custom", label: "Custom" },
];

const rangeForDays = (days) => [
  dayjs().subtract(days - 1, "day").startOf("day"),
  dayjs().endOf("day"),
];

const EMPTY_SUMMARY = {
  followers: 0,
  connections: 0,
  shares: 0,
  likes: 0,
  comments: 0,
  posts: 0,
  impressions: 0,
  membersReached: 0,
};

const toNumber = (value) => Number(value || 0);

// Was the LinkedIn account connected at this point in time, per the
// connect/disconnect event history? No events at all → treat the whole
// history as connected (avoids retroactively greying out clients whose
// account predates this feature). Otherwise, connected iff the most
// recent event at/before this timestamp was "connect".
const isConnectedAt = (timestampMs, events) => {
  if (!events || events.length === 0) return true;
  let connected = false;
  for (const e of events) {
    const eventTime = new Date(e.at).getTime();
    if (eventTime > timestampMs) break;
    connected = e.type === "connect";
  }
  return connected;
};

const buildRangeSummary = (records = []) => {
  if (!Array.isArray(records) || records.length === 0) {
    return { ...EMPTY_SUMMARY };
  }
  const latestRecord = [...records].sort((a, b) => {
    return (
      new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
    );
  })[0];
  return {
    followers: toNumber(latestRecord.followers),
    connections: toNumber(latestRecord.connections),
    shares: records.reduce((s, r) => s + toNumber(r.sharesDiff), 0),
    likes: records.reduce((s, r) => s + toNumber(r.likesDiff), 0),
    comments: records.reduce((s, r) => s + toNumber(r.commentsDiff), 0),
    posts: records.reduce((s, r) => s + toNumber(r.postsDiff), 0),
    impressions: toNumber(latestRecord.impressions),
    membersReached: toNumber(latestRecord.membersReached),
  };
};

const buildDateRangeSummaryFromHistory = (records = [], range) => {
  if (
    !Array.isArray(records) ||
    records.length === 0 ||
    !range ||
    range.length !== 2
  ) {
    return null;
  }
  const startValue = range[0].startOf("day").valueOf();
  const endValue = range[1].endOf("day").valueOf();
  const sorted = [...records].sort(
    (a, b) =>
      new Date(a.date || a.created_at) - new Date(b.date || b.created_at)
  );
  const inRange = sorted.filter((r) => {
    const t = new Date(r.date || r.created_at).getTime();
    return t >= startValue && t <= endValue;
  });
  if (!inRange.length) return null;
  return buildRangeSummary(inRange);
};

const getContentDate = (item) => {
  const v =
    item?.selectedDate ||
    item?.scheduleDate ||
    item?.approved_at ||
    item?.TimestampTime ||
    item?.created_at ||
    item?.updated_at;
  if (!v) return null;
  const p = dayjs(v);
  return p.isValid() ? p : null;
};

const isPublishedContent = (item) =>
  Boolean(item?.postId && String(item.postId).trim());

const filterContentByRange = (items = [], range) => {
  if (!Array.isArray(items)) return [];
  if (!range || range.length !== 2) return items;
  const start = range[0].startOf("day").valueOf();
  const end = range[1].endOf("day").valueOf();
  return items.filter((item) => {
    const d = getContentDate(item);
    if (!d) return false;
    return d.valueOf() >= start && d.valueOf() <= end;
  });
};

// Permission helper
const getPermissionByName = (moduleName) => {
  try {
    const user = JSON.parse(localStorage.getItem("admin_userProfile") || "{}");
    if (user.type === "Admin") {
      return { view: true, add: true, edit: true, delete: true };
    }
    // Case/whitespace-insensitive match — see BlogTabs.js's identical fix.
    const normalizedTarget = String(moduleName || "").trim().toLowerCase();
    const permissions = user?.roleId?.permission || [];
    return (
      permissions.find(
        (p) => String(p?.name || "").trim().toLowerCase() === normalizedTarget
      ) || {}
    );
  } catch {
    return {};
  }
};

const ReportsTabs = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Client ID
  const { request } = useRequest();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [userProfile, setUserProfile] = useState({});
  const [chartData, setChartData] = useState([]);
  const [connectionEvents, setConnectionEvents] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState("followers");
  const [dateRange, setDateRange] = useState(() => rangeForDays(7)); // Defaults to last 7 days
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [summaryData, setSummaryData] = useState({ ...EMPTY_SUMMARY });
  const [lifetimeMetrics, setLifetimeMetrics] = useState({ ...EMPTY_SUMMARY });
  const [analyticsHistory, setAnalyticsHistory] = useState([]);
  const [clientPosts, setClientPosts] = useState([]);
  const [clientBlogs, setClientBlogs] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(true);
  const [periodOption, setPeriodOption] = useState("7");
  const [pendingPeriodOption, setPendingPeriodOption] = useState("7");
  const [pendingCustomRange, setPendingCustomRange] = useState(null);
  const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);
  const reportBootstrapRef = useRef("");

  const reportsPermission = getPermissionByName("Reports");

  useEffect(() => {
    if (!reportsPermission?.view) navigate("/dashboard");
  }, [reportsPermission?.view]);

  // Published items (postId present)
  const publishedContentItems = useMemo(() => {
    const seen = new Set();
    return [...clientPosts, ...clientBlogs].filter((item) => {
      if (!isPublishedContent(item)) return false;
      const postId = String(item?.postId || "").trim();
      if (!postId || seen.has(postId)) return false;
      seen.add(postId);
      return true;
    });
  }, [clientPosts, clientBlogs]);

  // ── profile ───────────────────────────────────────────────────────────────
  const getProfileDetails = useCallback(() => {
    request({
      url: `/admin/access-module/get-clients/${id}`,
      method: "GET",
      onSuccess: (res) => {
        if (res.status === 200) setUserProfile(res.data.clients);
      },
      onError: (err) => console.error("Failed to fetch client profile", err),
    });
  }, [id]);

  // ── analytics history ─────────────────────────────────────────────────────
  const fetchAnalyticsHistory = useCallback(() => {
    request({
      url: `/admin/access-module/analytics/${id}`,
      method: "GET",
      onSuccess: (res) => {
        setAnalyticsHistory(Array.isArray(res?.data) ? res.data : []);
      },
      onError: () => setAnalyticsHistory([]),
    });
  }, [id]);

  const fetchClientPosts = useCallback(() => {
    request({
      url: `/admin/access-module/post/${id}?page=1&limit=1000`,
      method: "GET",
      onSuccess: (res) => setClientPosts(res?.data?.list || []),
      onError: () => setClientPosts([]),
    });
  }, [id]);

  const fetchClientBlogs = useCallback(() => {
    request({
      url: `/admin/access-module/blog/${id}?page=1&limit=1000`,
      method: "GET",
      onSuccess: (res) => setClientBlogs(res?.data?.blogs || []),
      onError: () => setClientBlogs([]),
    });
  }, [id]);

  // ── LinkedIn summary (lifetime + range) ───────────────────────────────────
  const fetchLinkedInSummary = useCallback(
    (range = null) => {
      const query =
        range && range.length === 2
          ? `?startDate=${range[0].format("YYYY-MM-DD")}&endDate=${range[1].format("YYYY-MM-DD")}`
          : "";
      setRangeLoading(true);
      request({
        url: `/admin/access-module/followers/${id}${query}`,
        method: "GET",
        onSuccess: (res) => {
          const items = res.data?.items || [];
          const postMetrics =
            res.data?.postMetrics || items[0]?.data?.postMetrics || {};
          const posts = Array.isArray(postMetrics.posts)
            ? postMetrics.posts
            : [];
          const followersCount = toNumber(
            res.data?.followers ?? items[0]?.data?.followers
          );
          const connectionsCount = toNumber(
            res.data?.connections ?? items[0]?.data?.connections
          );

          const liveSummary = {
            followers: followersCount,
            connections: connectionsCount,
            shares: toNumber(postMetrics.totalPostShares),
            likes: toNumber(postMetrics.totalPostLikes),
            comments: toNumber(postMetrics.totalPostComments),
            posts: toNumber(
              postMetrics.totalPosts ||
                filterContentByRange(publishedContentItems, range).length
            ),
            impressions: toNumber(postMetrics.totalImpressions),
            membersReached: toNumber(postMetrics.totalMembersReached),
          };

          if (range && range.length === 2) {
            setSummaryData(liveSummary);
            setRangeLoading(false);
            return;
          }

          setLifetimeMetrics(liveSummary);
          setReportData(posts);
          setRangeLoading(false);
        },
        onError: () => {
          if (range && range.length === 2) {
            const fallback = buildDateRangeSummaryFromHistory(
              analyticsHistory,
              range
            );
            if (fallback) {
              fallback.posts = filterContentByRange(
                publishedContentItems,
                range
              ).length;
              setSummaryData(fallback);
              setRangeLoading(false);
              return;
            }
            setSummaryData({ ...EMPTY_SUMMARY });
            setRangeLoading(false);
            return;
          }
          const fallback = buildRangeSummary(analyticsHistory);
          setLifetimeMetrics(fallback);
          setRangeLoading(false);
        },
      });
    },
    [id, analyticsHistory, publishedContentItems]
  );

  // ── bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    getProfileDetails();
  }, []);

  useEffect(() => {
    const accessToken = userProfile?.accessToken || "";
    if (!accessToken) {
      reportBootstrapRef.current = "";
      return;
    }
    if (reportBootstrapRef.current === accessToken) return;
    reportBootstrapRef.current = accessToken;
    // Only fetch the default period's worth of data (see dateRange effect
    // below) instead of the full lifetime/all-posts payload.
    fetchAnalyticsHistory();
    fetchClientPosts();
    fetchClientBlogs();
  }, [userProfile]);

  // Re-fetch when date range changes
  useEffect(() => {
    if (userProfile?.accessToken && dateRange && dateRange.length === 2) {
      fetchLinkedInSummary(dateRange);
    }
  }, [dateRange, userProfile]);

  // ── chart data ────────────────────────────────────────────────────────────
  const fetchChartData = useCallback(() => {
    const params = [];
    if (dateRange && dateRange.length === 2) {
      params.push(`startDate=${dateRange[0].format("YYYY-MM-DD")}`);
      params.push(`endDate=${dateRange[1].format("YYYY-MM-DD")}`);
    }
    if (selectedMetric) params.push(`metric=${selectedMetric}`);
    const url = `/admin/access-module/analytics/${id}${params.length ? "?" + params.join("&") : ""}`;
    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        setChartData(
          data.map((item) => ({
            ...item,
            date: item.date || item.created_at,
            followers: Number(item.followers || 0),
            connections: Number(item.connections || 0),
            shares: Number(item.shares || 0),
            likes: Number(item.likes || 0),
            comments: Number(item.comments || 0),
            posts: Number(item.posts || 0),
          }))
        );
        setConnectionEvents(Array.isArray(res?.connectionEvents) ? res.connectionEvents : []);
      },
      onError: () => {
        setChartData([]);
        setConnectionEvents([]);
      },
    });
  }, [id, dateRange, selectedMetric]);

  useEffect(() => {
    if (userProfile?.accessToken) fetchChartData();
  }, [selectedMetric, dateRange, userProfile, fetchChartData]);

  // ── Excel download ────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const token = localStorage.getItem("admin_token") || "";
      const hasRange = dateRange && dateRange.length === 2;
      let url = `${apiPath.baseURL}/admin/access-module/download/excel/${id}`;
      if (hasRange) {
        url += `?startDate=${dateRange[0].format("YYYY-MM-DD")}&endDate=${dateRange[1].format("YYYY-MM-DD")}`;
      }
      const response = await axios.get(url, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = hasRange
        ? `report_${dateRange[0].format("YYYY-MM-DD")}_to_${dateRange[1].format("YYYY-MM-DD")}.xlsx`
        : "report_lifetime.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      message.success("Report downloaded successfully!");
    } catch (error) {
      message.error("Failed to download report. Please try again.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  // Split the selected metric into two series so untracked periods (before
  // the account was ever connected, or during a disconnected stretch) can
  // render as a grey dashed line while tracked periods stay the normal
  // solid color — recharts has no per-point line styling, so two <Line>s
  // sharing an x-axis with complementary null gaps is the standard trick.
  // Each series repeats the boundary point on both sides of a transition so
  // the two lines visually connect instead of leaving a gap at the seam.
  const { splitChartData, connectionMarkers } = useMemo(() => {
    const withStatus = chartData.map((item) => {
      const t = item.date ? new Date(item.date).getTime() : NaN;
      return { ...item, __connected: Number.isFinite(t) ? isConnectedAt(t, connectionEvents) : true };
    });

    const split = withStatus.map((item, i) => {
      const value = item[selectedMetric];
      const prev = withStatus[i - 1];
      const next = withStatus[i + 1];
      const bridgesFromPrev = prev && prev.__connected !== item.__connected;
      const bridgesToNext = next && next.__connected !== item.__connected;
      return {
        ...item,
        metricConnected: item.__connected || bridgesFromPrev || bridgesToNext ? (item.__connected ? value : (bridgesFromPrev ? value : null)) : null,
        metricUntracked: !item.__connected || bridgesFromPrev || bridgesToNext ? (!item.__connected ? value : (bridgesFromPrev ? value : null)) : null,
      };
    });

    // XAxis here plots the raw `date` value (formatted only via
    // tickFormatter), so ReferenceLine's x must match that same raw value —
    // find the chart data point on the same calendar day as each event.
    const dayKey = (ms) => new Date(ms).toDateString();
    const markers = (connectionEvents || [])
      .map((e) => {
        const eventDay = dayKey(new Date(e.at).getTime());
        const match = withStatus.find((item) => item.date && dayKey(new Date(item.date).getTime()) === eventDay);
        return match ? { ...e, x: match.date } : null;
      })
      .filter(Boolean);

    return { splitChartData: split, connectionMarkers: markers };
  }, [chartData, connectionEvents, selectedMetric]);

  // ── display values ────────────────────────────────────────────────────────
  const allPosts = reportData;
  const hasDateRange = dateRange && dateRange.length === 2;

  const defaultTotalPosts =
    lifetimeMetrics.posts || publishedContentItems.length;
  const defaultTotalLikes =
    lifetimeMetrics.likes ||
    allPosts.reduce((s, p) => s + toNumber(p.numLikes), 0);
  const defaultTotalComments =
    lifetimeMetrics.comments ||
    allPosts.reduce((s, p) => s + toNumber(p.numComments), 0);
  const defaultTotalShares =
    lifetimeMetrics.shares ||
    allPosts.reduce((s, p) => s + toNumber(p.numShares), 0);
  const defaultTotalImpressions =
    lifetimeMetrics.impressions ||
    allPosts.reduce((s, p) => s + toNumber(p.numImpressions), 0);
  const defaultTotalMembersReached =
    lifetimeMetrics.membersReached ||
    allPosts.reduce((s, p) => s + toNumber(p.numMembersReached), 0);

  const displayFollowers = hasDateRange
    ? summaryData.followers
    : lifetimeMetrics.followers;
  const totalPosts = hasDateRange ? summaryData.posts : defaultTotalPosts;
  const totalLikes = hasDateRange ? summaryData.likes : defaultTotalLikes;
  const totalComments = hasDateRange
    ? summaryData.comments
    : defaultTotalComments;
  const totalShares = hasDateRange ? summaryData.shares : defaultTotalShares;
  const totalImpressions = hasDateRange
    ? toNumber(summaryData.impressions)
    : defaultTotalImpressions;
  const totalMembersReached = hasDateRange
    ? toNumber(summaryData.membersReached)
    : defaultTotalMembersReached;

  if (!userProfile?.accessToken) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          fontSize: 24,
          fontWeight: "bold",
          color: "#666",
        }}
      >
        LinkedIn is not connected
      </div>
    );
  }

  return (
    <div className="report-outer-main">
      <div className="report-section-01">
        {/* Header */}
        <div className="repport-headding-sec">
          <div className="headding-titals">
            <h4>
              {lang("Analytics")} * {lang(
                periodOption === "custom" && dateRange && dateRange.length === 2
                  ? `${dateRange[0].format("MMM D")} - ${dateRange[1].format("MMM D")}`
                  : `${PERIOD_OPTIONS.find((o) => o.value === periodOption)?.label || "7 days"} Data`,
              )}
            </h4>
          </div>
          <div className="report-headding-action">
            <div
              className="top-actions"
              style={{ display: "flex", gap: "12px", alignItems: "center" }}
            >
              <Dropdown
                trigger={["click"]}
                open={periodDropdownOpen}
                onOpenChange={(open) => {
                  if (open) {
                    setPendingPeriodOption(periodOption);
                    setPendingCustomRange(periodOption === "custom" ? dateRange : null);
                  }
                  setPeriodDropdownOpen(open);
                }}
                overlay={(
                  <div className="report-period-dropdown">
                    <Radio.Group
                      value={pendingPeriodOption}
                      onChange={(e) => setPendingPeriodOption(e.target.value)}
                      style={{ display: "flex", flexDirection: "column", width: "100%" }}
                    >
                      {PERIOD_OPTIONS.map((opt) => (
                        <Radio key={opt.value} value={opt.value}>
                          {lang(opt.label)}
                        </Radio>
                      ))}
                    </Radio.Group>
                    {pendingPeriodOption === "custom" && (
                      <div className="report-period-custom-range">
                        <RangePicker
                          placeholder={[lang("Start Date"), lang("End Date")]}
                          value={pendingCustomRange}
                          onChange={setPendingCustomRange}
                          format="YYYY-MM-DD"
                          style={{ width: "100%" }}
                          // Reports can't show data for days that haven't
                          // happened yet — block any date after today.
                          disabledDate={(current) => current && current > dayjs().endOf("day")}
                        />
                      </div>
                    )}
                    <div className="report-period-footer">
                      <Button
                        type="primary"
                        block
                        shape="round"
                        disabled={pendingPeriodOption === "custom" && (!pendingCustomRange || pendingCustomRange.length !== 2)}
                        onClick={() => {
                          if (pendingPeriodOption === "custom") {
                            setDateRange(pendingCustomRange);
                          } else {
                            setDateRange(rangeForDays(Number(pendingPeriodOption)));
                          }
                          setPeriodOption(pendingPeriodOption);
                          setPeriodDropdownOpen(false);
                        }}
                      >
                        {lang("Show results")}
                      </Button>
                    </div>
                  </div>
                )}
              >
                <Button>
                  {lang(PERIOD_OPTIONS.find((o) => o.value === periodOption)?.label || "7 days")} ▾
                </Button>
              </Dropdown>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadExcel}
                loading={downloadingExcel}
              >
                {lang("Download Excel")}
              </Button>
            </div>
          </div>
        </div>

        {/* KPI Cards — 7 cards matching user panel */}
        <div className="report-count-grid">
          <div className={`content-card kpi-card kpi-followers${rangeLoading ? " kpi-card--loading" : ""}`}>
            <div className="content-idea-card">
              <h6>{lang("Followers")}</h6>
            </div>
            <div className="report-count-detail-bar">
              <h4>{displayFollowers}</h4>
              <h6>{lang("Total Followers")}</h6>
            </div>
          </div>

          <div className={`content-card kpi-card kpi-posts${rangeLoading ? " kpi-card--loading" : ""}`}>
            <div className="content-idea-card">
              <h6>{lang("Published Posts")}</h6>
            </div>
            <div className="report-count-detail-bar">
              <h4>{totalPosts}</h4>
              <h6>{lang("Posts Published")}</h6>
            </div>
          </div>

          <div className={`content-card kpi-card kpi-likes${rangeLoading ? " kpi-card--loading" : ""}`}>
            <div className="content-idea-card">
              <h6>{lang("Likes")}</h6>
            </div>
            <div className="report-count-detail-bar">
              <h4>{totalLikes.toLocaleString()}</h4>
              <h6>{lang("Likes Received")}</h6>
            </div>
          </div>

          <div className={`content-card kpi-card kpi-comments${rangeLoading ? " kpi-card--loading" : ""}`}>
            <div className="content-idea-card">
              <h6>{lang("Comments")}</h6>
            </div>
            <div className="report-count-detail-bar">
              <h4>{totalComments.toLocaleString()}</h4>
              <h6>{lang("Comments Received")}</h6>
            </div>
          </div>

          <div className={`content-card kpi-card kpi-shares${rangeLoading ? " kpi-card--loading" : ""}`}>
            <div className="content-idea-card">
              <h6>{lang("Reposts / Shares")}</h6>
            </div>
            <div className="report-count-detail-bar">
              <h4>{totalShares.toLocaleString()}</h4>
              <h6>{lang("Shared by Audience")}</h6>
            </div>
          </div>

          <div className={`content-card kpi-card kpi-impressions${rangeLoading ? " kpi-card--loading" : ""}`}>
            <div className="content-idea-card">
              <h6>{lang("Impressions")}</h6>
            </div>
            <div className="report-count-detail-bar">
              <h4>{toNumber(totalImpressions).toLocaleString()}</h4>
              <h6>{lang("Times Shown in Feed")}</h6>
            </div>
          </div>

          <div className={`content-card kpi-card kpi-reach${rangeLoading ? " kpi-card--loading" : ""}`}>
            <div className="content-idea-card">
              <h6>{lang("Reach")}</h6>
            </div>
            <div className="report-count-detail-bar">
              <h4>{toNumber(totalMembersReached).toLocaleString()}</h4>
              <h6>{lang("Unique Members Reached")}</h6>
            </div>
          </div>
        </div>

        {/* Analytics Graph */}
        <div className="report-section-02" style={{ marginTop: 40 }}>
          <Card title="Analytics Graph" bordered={false} loading={rangeLoading}>
            <div
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 20,
                flexWrap: "wrap",
              }}
            >
              <Select
                value={selectedMetric}
                onChange={setSelectedMetric}
                style={{ width: 200 }}
                placeholder="Select Metric"
              >
                <Option value="followers">Followers</Option>
                <Option value="posts">Posts</Option>
                <Option value="shares">Shares</Option>
                <Option value="likes">Likes</Option>
                <Option value="comments">Comments</Option>
              </Select>

              {hasDateRange ? (
                <Tag color="blue">
                  {dateRange[0].format("YYYY-MM-DD")} to{" "}
                  {dateRange[1].format("YYYY-MM-DD")}
                </Tag>
              ) : (
                <Tag color="geekblue">
                  Showing last 30 days (select a date range above to filter)
                </Tag>
              )}
            </div>

            {connectionEvents.length > 0 && (
              <div className="analytics-chart-legend">
                <span className="analytics-chart-legend-item">
                  <span className="analytics-chart-legend-dot analytics-chart-legend-dot-tracked" />
                  Tracked (LinkedIn connected)
                </span>
                <span className="analytics-chart-legend-item">
                  <span className="analytics-chart-legend-dot analytics-chart-legend-dot-untracked" />
                  Untracked (not connected)
                </span>
                {connectionMarkers.length > 0 && (
                  <span className="analytics-chart-legend-item analytics-chart-legend-hint">
                    <InfoCircleOutlined /> Hover the marker on the chart for connection details
                  </span>
                )}
              </div>
            )}

            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={splitChartData} margin={{ top: 36, right: 24, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    if (!value) return "";
                    return new Date(value).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });
                  }}
                  tick={{ fontSize: 11, fill: "#8a94a6" }}
                  axisLine={{ stroke: "#e2e6ee" }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#8a94a6" }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 10,
                    border: "1px solid #e2e6ee",
                    boxShadow: "0 4px 16px rgba(20, 30, 60, 0.08)",
                    fontSize: 12,
                  }}
                  labelStyle={{ fontWeight: 600, color: "#1a3166", marginBottom: 4 }}
                  labelFormatter={(value) => {
                    if (!value) return value;
                    return new Date(value).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    });
                  }}
                  formatter={(value, name) => {
                    const metricLabel = selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1);
                    return [value, name === "metricUntracked" ? `${metricLabel} (untracked)` : metricLabel];
                  }}
                />
                {connectionMarkers.map((m, i) => {
                  const isConnect = m.type === "connect";
                  const color = isConnect ? "#16a34a" : "#dc2626";
                  const eventDate = new Date(m.at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  });
                  const tooltipText = isConnect
                    ? `LinkedIn connected to the app — ${eventDate}`
                    : `LinkedIn disconnected — ${eventDate}`;
                  return (
                    <ReferenceLine
                      key={i}
                      x={m.x}
                      stroke={color}
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      strokeOpacity={0.9}
                      // Custom label — a hoverable marker dot at the top of
                      // the line (native SVG <title> gives a free hover
                      // tooltip, no extra state needed) instead of plain
                      // text, which used to blend into the grid lines and
                      // get clipped by the chart's default top margin.
                      label={(props) => {
                        const { viewBox } = props;
                        const cx = viewBox.x;
                        const cy = viewBox.y + 10;
                        return (
                          <g style={{ cursor: "pointer" }}>
                            <circle cx={cx} cy={cy} r={9} fill={color} fillOpacity={0.15} />
                            <circle cx={cx} cy={cy} r={5.5} fill={color} stroke="#fff" strokeWidth={2}>
                              <title>{tooltipText}</title>
                            </circle>
                          </g>
                        );
                      }}
                    />
                  );
                })}
                <Line
                  type="monotone"
                  dataKey="metricConnected"
                  name="metricConnected"
                  stroke="#6c63f5"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#6c63f5", strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="metricUntracked"
                  name="metricUntracked"
                  stroke="#bfc5d2"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={{ r: 2.5, fill: "#bfc5d2", strokeWidth: 0 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReportsTabs;
